import {
  forwardRef,
  useEffect,
  useId,
  useRef,
  type ClipboardEvent,
  type KeyboardEvent,
} from 'react';
import { useTranslation } from 'react-i18next';

import { cn } from '@/utils/cn';

export interface OtpInputProps {
  value: string;
  onChange: (value: string) => void;
  /** Number of digit boxes. Defaults to 6. */
  length?: number;
  disabled?: boolean;
  invalid?: boolean;
  autoFocus?: boolean;
  id?: string;
  'aria-label'?: string;
  'aria-labelledby'?: string;
  'aria-describedby'?: string;
  className?: string;
  /** Fires once when every box is filled with a digit. */
  onComplete?: (value: string) => void;
}

/**
 * Fixed-length one-time code entry as individual digit boxes. Used for
 * authenticator codes at sign-in and step-up confirmation.
 */
export const OtpInput = ({
  value,
  onChange,
  length = 6,
  disabled = false,
  invalid = false,
  autoFocus = false,
  id,
  'aria-label': ariaLabel,
  'aria-labelledby': ariaLabelledBy,
  'aria-describedby': ariaDescribedBy,
  className,
  onComplete,
}: OtpInputProps) => {
  const { t } = useTranslation();
  const reactId = useId();
  const groupId = id ?? reactId;
  const inputsRef = useRef<Array<HTMLInputElement | null>>([]);
  const digits = Array.from({ length }, (_, index) => value[index] ?? '');

  useEffect(() => {
    if (!autoFocus || disabled) return;
    inputsRef.current[0]?.focus();
  }, [autoFocus, disabled]);

  const emit = (joined: string) => {
    const next = joined.slice(0, length);
    onChange(next);
    if (next.length === length && /^\d+$/.test(next)) {
      onComplete?.(next);
    }
  };

  const focusAt = (index: number) => {
    const clamped = Math.max(0, Math.min(length - 1, index));
    inputsRef.current[clamped]?.focus();
    inputsRef.current[clamped]?.select();
  };

  const applyDigits = (startIndex: number, chars: string[]) => {
    const next = digits.slice();
    chars.forEach((char, offset) => {
      next[startIndex + offset] = char;
    });
    emit(next.join(''));
    focusAt(Math.min(startIndex + chars.length, length - 1));
  };

  const handleChange = (index: number, raw: string) => {
    const cleaned = raw.replace(/\D/g, '');
    if (!cleaned) {
      const next = digits.slice();
      next[index] = '';
      onChange(next.join(''));
      return;
    }

    if (cleaned.length > 1) {
      applyDigits(index, cleaned.slice(0, length - index).split(''));
      return;
    }

    const next = digits.slice();
    next[index] = cleaned;
    emit(next.join(''));
    if (index < length - 1) focusAt(index + 1);
  };

  const handleKeyDown = (index: number, event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Backspace') {
      event.preventDefault();
      if (digits[index]) {
        const next = digits.slice();
        next[index] = '';
        onChange(next.join(''));
        return;
      }
      if (index > 0) {
        const next = digits.slice();
        next[index - 1] = '';
        onChange(next.join(''));
        focusAt(index - 1);
      }
      return;
    }

    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      focusAt(index - 1);
      return;
    }

    if (event.key === 'ArrowRight') {
      event.preventDefault();
      focusAt(index + 1);
      return;
    }

    if (event.key === 'Home') {
      event.preventDefault();
      focusAt(0);
      return;
    }

    if (event.key === 'End') {
      event.preventDefault();
      focusAt(length - 1);
    }
  };

  const handlePaste = (index: number, event: ClipboardEvent<HTMLInputElement>) => {
    event.preventDefault();
    const pasted = event.clipboardData.getData('text').replace(/\D/g, '');
    if (!pasted) return;
    applyDigits(index, pasted.slice(0, length - index).split(''));
  };

  return (
    <div
      role="group"
      id={groupId}
      aria-label={ariaLabel}
      aria-labelledby={ariaLabelledBy}
      aria-describedby={ariaDescribedBy}
      className={cn('flex justify-center gap-2', className)}
    >
      {digits.map((digit, index) => (
        <OtpBox
          key={`${groupId}-${index}`}
          ref={(node) => {
            inputsRef.current[index] = node;
          }}
          digitLabel={t('otp.digit', { number: index + 1 })}
          value={digit}
          disabled={disabled}
          invalid={invalid}
          autoComplete={index === 0 ? 'one-time-code' : 'off'}
          onChange={(raw) => handleChange(index, raw)}
          onKeyDown={(event) => handleKeyDown(index, event)}
          onPaste={(event) => handlePaste(index, event)}
          onFocus={() => inputsRef.current[index]?.select()}
        />
      ))}
    </div>
  );
};

const OtpBox = forwardRef<
  HTMLInputElement,
  {
    digitLabel: string;
    value: string;
    disabled: boolean;
    invalid: boolean;
    autoComplete: string;
    onChange: (raw: string) => void;
    onKeyDown: (event: KeyboardEvent<HTMLInputElement>) => void;
    onPaste: (event: ClipboardEvent<HTMLInputElement>) => void;
    onFocus: () => void;
  }
>(function OtpBox(
  { digitLabel, value, disabled, invalid, autoComplete, onChange, onKeyDown, onPaste, onFocus },
  ref,
) {
  return (
    <input
      ref={ref}
      type="text"
      inputMode="numeric"
      pattern="[0-9]*"
      maxLength={1}
      autoComplete={autoComplete}
      disabled={disabled}
      aria-label={digitLabel}
      aria-invalid={invalid || undefined}
      value={value}
      onChange={(event) => onChange(event.target.value)}
      onKeyDown={onKeyDown}
      onPaste={onPaste}
      onFocus={onFocus}
      className={cn(
        'size-11 rounded-md border bg-surface text-center font-mono text-card-title text-fg outline-none transition-colors sm:size-12',
        'focus:border-brand focus:ring-2 focus:ring-brand/20',
        invalid ? 'border-danger' : 'border-border hover:border-border-strong',
        disabled && 'cursor-not-allowed bg-surface-sunken text-fg-disabled',
      )}
    />
  );
});
